import { persistReducer, Storage } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import {
  SystemConfigurationActionTypes,
  ConfigurationActionTypes,
  ConfigurationState,
  Component,
  ShopSystemConfiguration,
  Configuration,
  RetrofitBattery,
  PowerMeter,
  ShippingDate,
  SmartMeter,
} from './ShopSystemConfigurationModel'
import { ProductGeneralType } from 'src/Libs/shop/types'

/*
 * Define the initial state of the system configurator
 */
const initialState: ConfigurationState = {
  items: [
    {
      type: ProductGeneralType.SYSTEM_CONFIGURATOR,
      quantity: 1,
      label: '',
      desiredShippingDate: {
        day: null,
        month: null,
        year: null,
      },
      configuration: {
        configuratorId: null,
        components: [],
        powerMeters: [],
        retrofitBattery: null,
        smartMeter: null,
      },
    },
  ],
}

const configurationReducer = (state = initialState, action: ConfigurationActionTypes): ConfigurationState => {
  switch (action.type) {
    case SystemConfigurationActionTypes.AddComponent: {
      const newComponents: Component[] = [...state.items[0].configuration.components, action.payload]
      const newConfiguration: Configuration = {
        ...state.items[0].configuration,
        components: newComponents,
      }
      const newItems: ShopSystemConfiguration[] = [
        {
          ...state.items[0],
          configuration: newConfiguration,
        },
      ]
      return {
        ...state,
        items: newItems,
      }
    }
    case SystemConfigurationActionTypes.RemoveComponent: {
      const { payload: componentId } = action
      const newComponents: Component[] = state.items[0].configuration.components.filter(component => component.id !== componentId)
      const newConfiguration: Configuration = {
        ...state.items[0].configuration,
        components: newComponents,
      }
      const newItems: ShopSystemConfiguration[] = [
        {
          ...state.items[0],
          configuration: newConfiguration,
        },
      ]
      return {
        ...state,
        items: newItems,
      }
    }
    case SystemConfigurationActionTypes.AddProduct: {
      const { componentId, productId, qty } = action.payload

      const existingComponentIndex = state.items[0].configuration.components.findIndex(component => component.id === componentId)

      if (existingComponentIndex !== -1) {
        /* Component exists, update the products array*/
        const updatedComponents = state.items[0].configuration.components.map((component, index) => {
          if (index === existingComponentIndex) {
            return {
              ...component,
              products: [
                ...component.products,
                {
                  id: productId,
                  qty,
                },
              ],
            }
          }
          return component
        })

        const newConfiguration: Configuration = {
          ...state.items[0].configuration,
          components: updatedComponents,
        }

        const newItems: ShopSystemConfiguration[] = [
          {
            ...state.items[0],
            configuration: newConfiguration,
          },
        ]

        return {
          ...state,
          items: newItems,
        }
      } else {
        /* Component doesn't exist, create a new one with the given ID*/
        const newComponent: Component = {
          id: componentId,
          products: [
            {
              id: productId,
              qty,
            },
          ],
        }

        const newConfiguration: Configuration = {
          ...state.items[0].configuration,
          components: [...state.items[0].configuration.components, newComponent],
        }

        const newItems: ShopSystemConfiguration[] = [
          {
            ...state.items[0],
            configuration: newConfiguration,
          },
        ]

        return {
          ...state,
          items: newItems,
        }
      }
    }
    case SystemConfigurationActionTypes.RemoveProduct: {
      const { componentId, productId } = action.payload

      /* Check if the component with the given ID exists*/
      const componentIndex = state.items[0].configuration.components.findIndex(component => component.id === componentId)

      if (componentIndex !== -1) {
        /* Component exists, remove the product from the products array*/
        const updatedComponents: Component[] = [...state.items[0].configuration.components]
        updatedComponents[componentIndex] = {
          ...updatedComponents[componentIndex],
          products: updatedComponents[componentIndex].products.filter(product => product.id !== productId),
        }

        const newConfiguration: Configuration = {
          ...state.items[0].configuration,
          components: updatedComponents,
        }

        const newItems: ShopSystemConfiguration[] = [
          {
            ...state.items[0],
            configuration: newConfiguration,
          },
        ]

        return {
          ...state,
          items: newItems,
        }
      } else {
        /* Component doesn't exist, no changes required*/
        return state
      }
    }
    case SystemConfigurationActionTypes.IncreaseQuantity: {
      const { componentId, productId } = action.payload

      const updatedComponents: Component[] = state.items[0].configuration.components.map(component => {
        if (component.id === componentId) {
          const updatedProducts = component.products.map(product => {
            if (product.id === productId) {
              return {
                ...product,
                qty: product.qty + 1,
              }
            }
            return product
          })

          return {
            ...component,
            products: updatedProducts,
          }
        }
        return component
      })

      const newConfiguration: Configuration = {
        ...state.items[0].configuration,
        components: updatedComponents,
      }

      const newItems: ShopSystemConfiguration[] = [
        {
          ...state.items[0],
          configuration: newConfiguration,
        },
      ]

      return {
        ...state,
        items: newItems,
      }
    }
    case SystemConfigurationActionTypes.DecreaseQuantity: {
      const { componentId, productId } = action.payload

      const updatedComponents: Component[] = state.items[0].configuration.components.map(component => {
        if (component.id === componentId) {
          const updatedProducts = component.products.map(product => {
            if (product.id === productId) {
              /* Ensure the quantity doesn't go below 0*/
              return {
                ...product,
                qty: Math.max(0, product.qty - 1),
              }
            }
            return product
          })

          return {
            ...component,
            products: updatedProducts,
          }
        }
        return component
      })

      const newConfiguration: Configuration = {
        ...state.items[0].configuration,
        components: updatedComponents,
      }

      const newItems: ShopSystemConfiguration[] = [
        {
          ...state.items[0],
          configuration: newConfiguration,
        },
      ]

      return {
        ...state,
        items: newItems,
      }
    }

    case SystemConfigurationActionTypes.AddRetrofitBattery: {
      const { id, qty } = action.payload

      const newRetrofitBattery: RetrofitBattery = {
        id: id,
        qty,
      }

      const newConfiguration: Configuration = {
        ...state.items[0].configuration,
        retrofitBattery: newRetrofitBattery,
      }

      const newItems: ShopSystemConfiguration[] = [
        {
          ...state.items[0],
          configuration: newConfiguration,
        },
      ]

      return {
        ...state,
        items: newItems,
      }
    }
    case SystemConfigurationActionTypes.RemoveRetrofitBattery: {
      const newConfiguration: Configuration = {
        ...state.items[0].configuration,
        retrofitBattery: null,
      }

      const newItems: ShopSystemConfiguration[] = [
        {
          ...state.items[0],
          configuration: newConfiguration,
        },
      ]

      return {
        ...state,
        items: newItems,
      }
    }

    case SystemConfigurationActionTypes.AddSmartMeter: {
      const { id } = action.payload

      const newSmartMeter: SmartMeter = {
        id: id,
      }

      const newConfiguration: Configuration = {
        ...state.items[0].configuration,
        smartMeter: newSmartMeter,
      }

      const newItems: ShopSystemConfiguration[] = [
        {
          ...state.items[0],
          configuration: newConfiguration,
        },
      ]

      return {
        ...state,
        items: newItems,
      }
    }

    case SystemConfigurationActionTypes.RemoveSmartMeter: {
      const newConfiguration: Configuration = {
        ...state.items[0].configuration,
        smartMeter: null,
      }

      const newItems: ShopSystemConfiguration[] = [
        {
          ...state.items[0],
          configuration: newConfiguration,
        },
      ]

      return {
        ...state,
        items: newItems,
      }
    }

    case SystemConfigurationActionTypes.AddPowerMeter: {
      const newPowerMeters: PowerMeter[] = [...state.items[0].configuration.powerMeters, action.payload]
      const newConfiguration: Configuration = {
        ...state.items[0].configuration,
        powerMeters: newPowerMeters,
      }
      const newItems: ShopSystemConfiguration[] = [
        {
          ...state.items[0],
          configuration: newConfiguration,
        },
      ]
      return {
        ...state,
        items: newItems,
      }
    }
    case SystemConfigurationActionTypes.RemovePowerMeter: {
      const { payload: powerMeterId } = action
      const newPowerMeters: PowerMeter[] = state.items[0].configuration.powerMeters.filter(powerMeter => powerMeter.powerMeterId !== powerMeterId)
      const newConfiguration: Configuration = {
        ...state.items[0].configuration,
        powerMeters: newPowerMeters,
      }
      const newItems: ShopSystemConfiguration[] = [
        {
          ...state.items[0],
          configuration: newConfiguration,
        },
      ]
      return {
        ...state,
        items: newItems,
      }
    }

    case SystemConfigurationActionTypes.SetQuantity: {
      const newItems: ShopSystemConfiguration[] = [
        {
          ...state.items[0],
          quantity: action.payload,
        },
      ]
      return {
        ...state,
        items: newItems,
      }
    }
    case SystemConfigurationActionTypes.UpdateLabel: {
      const newItems: ShopSystemConfiguration[] = [
        {
          ...state.items[0],
          label: action.payload,
        },
      ]
      return {
        ...state,
        items: newItems,
      }
    }
    case SystemConfigurationActionTypes.SetShippingDate: {
      const newDate: ShippingDate = {
        ...state.items[0].desiredShippingDate,
        day: action.payload.day,
        month: action.payload.month,
        year: action.payload.year,
      }
      const newItems: ShopSystemConfiguration[] = [{ ...state.items[0], desiredShippingDate: newDate }]
      return {
        ...state,
        items: newItems,
      }
    }

    case SystemConfigurationActionTypes.SetConfiguratorId:
      return {
        ...state,
        items: state.items.map(item => {
          if (item.type === 'configured_system') {
            return {
              ...item,
              configuration: {
                ...item.configuration,
                configuratorId: action.payload,
              },
            }
          }
          return item
        }),
      }
    case SystemConfigurationActionTypes.AddConfigurator:
      return {
        ...state,
        items: [action.payload],
      }
    case SystemConfigurationActionTypes.ResetConfigurator: {
      return initialState
    }
    default:
      return state
  }
}

const persistConfig: { key: string; storage: Storage; whitelist: (keyof ConfigurationState)[] } = {
  key: 'SystemConfiguration',
  storage: storage,
  whitelist: ['items'],
}

export default persistReducer(persistConfig, configurationReducer)
